/**
 * Denomination lens: a display-only transform for user-scoped USD monetary
 * values. State lives in the UI-config overlay; conversion happens only at the
 * value-format seam and never changes stored, queried, or backend-bound data.
 */

import {
  getResolvedUiConfig,
  subscribeToUiConfigOverlay,
  updateUiConfigOverlay,
} from "~/lib/ui-config";

export interface DenominationRegistryEntry {
  /**
   * @deprecated Number shape is resolved by getNumberFormattingLocale().
   * Retained only for registry compatibility with older overlay consumers.
   */
  readonly locale: string;
  readonly label: string;
  readonly flag: string;
  readonly inverseRate?: true;
}

function defineDenominationRegistry<const Currency extends string>(
  registry: Readonly<Record<Currency, DenominationRegistryEntry>>,
): Readonly<Record<Currency, DenominationRegistryEntry>> {
  return registry;
}

export const DENOMINATION_REGISTRY = defineDenominationRegistry({
  USD: { locale: "en-US", label: "US Dollar", flag: "🇺🇸" },
  EUR: { locale: "nl-NL", label: "Euro", flag: "🇪🇺" },
  BRL: { locale: "pt-BR", label: "Brazilian Real", flag: "🇧🇷" },
  CNY: { locale: "zh-CN", label: "Chinese Yuan", flag: "🇨🇳" },
  HKD: { locale: "zh-HK", label: "Hong Kong Dollar", flag: "🇭🇰" },
  KRW: { locale: "ko-KR", label: "South Korean Won", flag: "🇰🇷" },
  RUB: { locale: "ru-RU", label: "Russian Ruble", flag: "🇷🇺" },
  INR: { locale: "en-IN", label: "Indian Rupee", flag: "🇮🇳" },
  NGN: { locale: "en-NG", label: "Nigerian Naira", flag: "🇳🇬" },
  IDR: { locale: "id-ID", label: "Indonesian Rupiah", flag: "🇮🇩" },
  VND: { locale: "vi-VN", label: "Vietnamese Dong", flag: "🇻🇳" },
  PHP: { locale: "en-PH", label: "Philippine Peso", flag: "🇵🇭" },
  MXN: { locale: "es-MX", label: "Mexican Peso", flag: "🇲🇽" },
  ARS: { locale: "es-AR", label: "Argentine Peso", flag: "🇦🇷" },
  BTC: {
    locale: "en-US",
    label: "Bitcoin",
    flag: "₿",
    inverseRate: true,
  },
});

export type DisplayCurrency = keyof typeof DENOMINATION_REGISTRY;

type DenominationLensListener = () => void;

function isDisplayCurrency(currency: string): currency is DisplayCurrency {
  return Object.hasOwn(DENOMINATION_REGISTRY, currency);
}

export function getDenominationCurrency(): DisplayCurrency {
  const currency = getResolvedUiConfig().lenses.denomination.currency;
  return isDisplayCurrency(currency) ? currency : "USD";
}

export function setDenominationCurrency(currency: DisplayCurrency): void {
  if (getDenominationCurrency() === currency) {
    return;
  }

  updateUiConfigOverlay((overlay) => ({
    ...overlay,
    lenses: {
      ...overlay.lenses,
      denomination: { ...overlay.lenses?.denomination, currency },
    },
  }));
}

export function subscribeToDenominationLens(
  listener: DenominationLensListener,
): () => void {
  return subscribeToUiConfigOverlay(listener);
}
